import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus, Pencil, Trash2 } from 'lucide-react'
import { galleryApi } from '../../services/api'
import Button from '../../components/ui/Button'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import Card from '../../components/ui/Card'
import AdminFormField, { adminInputClass } from '../../components/admin/AdminFormField'
import AdminImageField from '../../components/admin/AdminImageField'
import MediaImage from '../../components/ui/MediaImage'
import { buildImageFormData } from '../../utils/adminFormData'

const EMPTY = { image: '', title_en: '', title_ar: '', sort_order: 0 }

function normalizeGallery(item) {
  return {
    ...item,
    title_en: item.title_en || item.caption_en || '',
    title_ar: item.title_ar || item.caption_ar || '',
    sort_order: item.sort_order ?? item.order ?? 0,
  }
}

function buildGalleryPayload(form, imageFile) {
  const base = {
    image: form.image || '',
    title_en: form.title_en,
    title_ar: form.title_ar,
    sort_order: form.sort_order ?? form.order ?? 0,
  }
  return buildImageFormData(base, form, imageFile)
}

export default function AdminGallery() {
  const { t } = useTranslation()
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [form, setForm] = useState(null)
  const [imageFile, setImageFile] = useState(null)
  const [imagePreview, setImagePreview] = useState(null)
  const [saving, setSaving] = useState(false)

  const resetImageState = () => {
    if (imagePreview?.startsWith('blob:')) URL.revokeObjectURL(imagePreview)
    setImageFile(null)
    setImagePreview(null)
  }

  const handleImageSelect = (file) => {
    if (imagePreview?.startsWith('blob:')) URL.revokeObjectURL(imagePreview)
    setImageFile(file)
    setImagePreview(URL.createObjectURL(file))
  }

  const load = () => {
    setLoading(true)
    galleryApi
      .getAllAdmin({ per_page: 100 })
      .then(({ data }) => setItems(data.data || data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false))
  }

  useEffect(load, [])

  const handleSave = async (e) => {
    e.preventDefault()
    setSaving(true)
    try {
      const payload = buildGalleryPayload(form, imageFile)
      if (form.id) await galleryApi.update(form.id, payload)
      else await galleryApi.create(payload)
      resetImageState()
      setForm(null)
      load()
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id) => {
    if (!window.confirm(t('admin.galleryAdmin.confirmDelete'))) return
    await galleryApi.delete(id)
    load()
  }

  return (
    <div>
      <div className="mb-8 flex items-center justify-between">
        <h1 className="text-3xl font-bold text-primary">{t('admin.galleryAdmin.title')}</h1>
        <Button onClick={() => { resetImageState(); setForm(EMPTY) }} variant="accent" size="sm">
          <Plus className="h-4 w-4" />
          {t('admin.galleryAdmin.add')}
        </Button>
      </div>

      {form && (
        <Card className="mb-8">
          <form onSubmit={handleSave} className="grid gap-4 sm:grid-cols-2">
            <AdminImageField
              label={t('admin.newsAdmin.image')}
              htmlFor="gallery-image"
              className="sm:col-span-2"
              value={form.image}
              onChange={(image) => setForm({ ...form, image })}
              imageFile={imageFile}
              imagePreview={imagePreview}
              onFileSelect={handleImageSelect}
              disabled={saving}
              required
            />
            <AdminFormField label={`${t('admin.galleryAdmin.caption')} (${t('admin.pages.titleEn')})`} htmlFor="gallery-title-en">
              <input id="gallery-title-en" value={form.title_en} onChange={(e) => setForm({ ...form, title_en: e.target.value })} className={adminInputClass} />
            </AdminFormField>
            <AdminFormField label={`${t('admin.galleryAdmin.caption')} (${t('admin.pages.titleAr')})`} htmlFor="gallery-title-ar">
              <input id="gallery-title-ar" value={form.title_ar} onChange={(e) => setForm({ ...form, title_ar: e.target.value })} dir="rtl" lang="ar" className={`${adminInputClass} font-ar`} />
            </AdminFormField>
            <div className="flex gap-2 sm:col-span-2">
              <Button type="submit" variant="primary" disabled={saving}>{t('buttons.save')}</Button>
              <Button type="button" variant="ghost" onClick={() => { resetImageState(); setForm(null) }}>{t('buttons.cancel')}</Button>
            </div>
          </form>
        </Card>
      )}

      {loading ? (
        <LoadingSpinner className="py-20" />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {items.map((item) => (
            <Card key={item.id} padding={false} className="overflow-hidden">
              <MediaImage
                src={item.image || item.image_url}
                alt={item.title_en || item.caption_en}
                className="aspect-square w-full object-cover"
                fallbackIndex={item.sort_order ?? 0}
              />
              <div className="flex items-center justify-between p-3">
                <span className="truncate text-sm text-text/70">{item.title_en || item.caption_en}</span>
                <div className="flex gap-1">
                  <button type="button" onClick={() => { resetImageState(); setForm(normalizeGallery(item)) }} className="p-1.5 text-primary"><Pencil className="h-4 w-4" /></button>
                  <button type="button" onClick={() => handleDelete(item.id)} className="p-1.5 text-red-600"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
